import {
    IconName,
    Table,
    TableActiveColumnFilters,
    TableBulkActionButton,
    TableBulkActions,
    TableHeader,
    TableProvider,
    useRowSelection,
} from '@optimus-web/core';
import { RoleFilters, RoleGetResult } from '@mews-commander/data-access';
import { LocalizedText, useDialog, useUrlSyncedTableState } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DeleteRolesDialog } from '../DeleteRole';
import { RoleDetail } from '../RoleDetail';
import { useRoleContext } from '../context';
import { isRoleDeletable, usePortfolioSelectors } from '../utils';
import { useRolesListData } from './queries';
import { useColumns } from './tableMapping';

export const RolesList = () => {
    const {
        rowSelection,
        setRowSelection,
        selectedRowsIds,
        selectedRowsCount,
        hasRowsSelected,
        unselectRow,
        resetRowSelection,
    } = useRowSelection();

    const { activeRole } = useRoleContext();

    const columns = useColumns({ onRowDelete: unselectRow });

    const [tableState, setTableState] = useUrlSyncedTableState<RoleGetResult, RoleFilters>({ columns, pageSize: 50 });

    const { data, isFetching } = useRolesListData(tableState);
    const roles = data?.data || [];
    const totalRowCount = data?.totalRowCount ?? 0;

    const { isPortfolio } = usePortfolioSelectors();

    const [deleteRolesDialogProps, openDeleteRolesDialog] = useDialog();

    return (
        <>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                <TableHeader
                    title={<LocalizedText textKey={TextKey.Roles} />}
                    subTitle={totalRowCount.toString()}
                    globalSearch={{ placeholder: <LocalizedText textKey={TextKey.RolesSearchHint} /> }}
                />

                <TableActiveColumnFilters />

                <TableBulkActions
                    disabled={!hasRowsSelected}
                    leftSide={
                        hasRowsSelected ? (
                            <LocalizedText textKey={TextKey.SelectedItems} substitutes={{ count: selectedRowsCount }} />
                        ) : null
                    }
                    rightSide={
                        <TableBulkActionButton
                            icon={IconName.Delete}
                            disabled={!hasRowsSelected}
                            onClick={openDeleteRolesDialog}
                        >
                            <LocalizedText textKey={TextKey.Delete} />
                        </TableBulkActionButton>
                    }
                />

                <Table
                    data={roles}
                    amountOfRows={totalRowCount}
                    loading={isFetching}
                    uniqueRowId="id"
                    rowSelection={{
                        rowSelection,
                        onRowSelectionChange: setRowSelection,
                        enableRowSelection: (row) => isRoleDeletable(row.original, isPortfolio),
                    }}
                    fixedFirstColumn
                />
            </TableProvider>

            {activeRole ? <RoleDetail /> : null}

            <DeleteRolesDialog roleIds={selectedRowsIds} onSuccess={resetRowSelection} {...deleteRolesDialogProps} />
        </>
    );
};
